<template>
    <layout-modal
        ref="modal"
        modal-classes="bg-black!"
        size="sm:max-w-md"
        @closing="onClose"
    >
        <template #default>
            <div
                :class="isCouponActive ? '-m-8 lg:-m-12 sm:-mt-6 p-10 lg:p-12 text-white rounded-2xl' : ''"
                class="flex flex-col items-center text-center"
            >
                <img
                    v-if="isCouponActive"
                    alt="Access: Houses of KaDeWe"
                    class="w-24 h-auto mb-8 rounded-2xl"
                    src="/images/promotion/app-banner-icon.png"
                />
                <img
                    v-else
                    alt="Die Access: Houses of KaDeWe App"
                    class="w-full h-auto mb-6 rounded"
                    src="/images/promotion/app-banner-neutral.png"
                />

                <div v-if="isCouponActive">
                    <h3
                        class="style-h3 tracking-wide text-white mb-0"
                        v-text="$t('app-promo-banner.coupon.headline')"
                    />
                    <h3
                        class="style-h3 tracking-wide"
                        v-text="$t('app-promo-banner.coupon.title')"
                    />

                    <h3
                        class="style-h3 tracking-wide"
                        v-html="$t('app-promo-banner.coupon.subtitle')"
                    />
                </div>
                <div v-else>
                    <h3
                        class="style-h4 tracking-wide"
                        v-text="$t('app-promo-banner.neutral.title')"
                    />
                </div>

                <p
                    class="mt-4 style-h6"
                    v-text="
                        isCouponActive
                            ? $t('app-promo-banner.coupon.description')
                            : $t('app-promo-banner.neutral.description')
                    "
                />

                <div class="flex items-center gap-4 mt-10">
                    <a
                        :href="androidStoreUrl"
                        rel="noopener"
                        target="_blank"
                        @click="trackDownloadClick('android')"
                    >
                        <img
                            alt="Jetzt bei Google Play"
                            class="h-10 w-auto"
                            src="/images/loyalty-programm/google-play-badge.svg"
                        />
                    </a>
                    <a
                        :href="iOSStoreUrl"
                        rel="noopener"
                        target="_blank"
                        @click="trackDownloadClick('ios')"
                    >
                        <img
                            alt="Laden im App Store"
                            class="h-10 w-auto"
                            src="/images/loyalty-programm/app-store-badge.svg"
                        />
                    </a>
                </div>

                <a
                    class="mt-8 style-body-default underline underline-offset-2 cursor-pointer mb-4"
                    @click="modal?.close()"
                    v-text="$t('app-promo-banner.dismiss')"
                />
            </div>
        </template>
    </layout-modal>
</template>

<script lang="ts" setup>
import { useCookies } from "@vueuse/integrations/useCookies";
import type { LayoutModal } from "#components"; // First design (app coupon) runs until the coupon expires on 2026-10-04, the neutral design takes over from 2026-10-05.

// First design (app coupon) runs until the coupon expires on 2026-10-04, the neutral design takes over from 2026-10-05.
const COUPON_VALID_UNTIL = new Date("2026-10-05T00:00:00");
const REMIND_AFTER_DAYS = 14;

const config = useRuntimeConfig();
const iOSStoreUrl = config.public.app.iOSStoreUrl;
const androidStoreUrl = config.public.app.androidStoreUrl;
const cookies = useCookies();
const cookieStore = useCookieStore();
const { isMobileDevice } = useIsMobileDevice();

const modal = ref<InstanceType<typeof LayoutModal>>();

const isCouponActive = computed(() => new Date() < COUPON_VALID_UNTIL);

onMounted(() => {
    if (!isMobileDevice() || cookies.get("app_promo_banner_dismissed") !== undefined) {
        return;
    }

    modal.value?.open();
    cookieStore.trackEvent("app_promo_banner_shown", {
        variant: isCouponActive.value ? "coupon" : "neutral",
    });
});

const trackDownloadClick = (store: "ios" | "android") => {
    cookieStore.trackEvent("app_promo_banner_download_click", { store });
};

const onClose = () => {
    cookieStore.trackEvent("app_promo_banner_closed");
    cookies.set("app_promo_banner_dismissed", "true", {
        expires: new Date(Date.now() + REMIND_AFTER_DAYS * 24 * 60 * 60 * 1000),
    });
};
</script>
